import { useEffect, useState } from 'react'
import type { Change } from '@/realtime/stream'
import { useFallback, useRefetchOn } from './useEventStream'
import { usePoll } from './usePoll'
import { useRefreshAfterWrites } from './useWorld'

// 커지는 목록의 한 쪽 — 서버가 거르고 찾고 세운 목록에서 지금 보는 쪽만 읽는다(작업 내역과 같은 offset 쪽).
// 목록 전체를 받아 화면에서 거르고 세우면 VM·사람이 수천인 배포에서 폴링마다 그 전부가 오간다.
//
// - 읽기는 `usePoll` 이다. 그 목록이 싣는 kind 의 변경 알림(`match`)에는 1초 모아 지금 쪽을 다시 읽고,
//   쓰기 뒤의 다시 읽기(`useWorldRefresh`)에도 단다 — 관리자(제한)는 스트림이 없어 그것이 유일한 즉시 읽기다.
// - 거르개·정렬(`filters`)이나 찾는 말이 바뀌면 1쪽으로 돌아간다 — 다른 목록이다.
//   목록이 줄어 지금 쪽이 끝을 넘으면 마지막 쪽으로 당긴다.
// - 찾는 말은 250ms 모은다 — 치는 글자마다 묻지 않는다. 처음 값은 기다리지 않는다.
// - 한 쪽은 그 순간의 목록이다: 쪽을 옮기는 사이 들어오고 나간 줄이 나머지를 한 칸씩 민다.
// 같은 응답의 줄은 이전 객체 그대로다(`usePoll` 의 구조 공유) — `memo` 줄은 바뀐 줄만 다시 그린다.

/** 한 쪽에 싣는 줄 수. 작업 내역과 같은 셋이다. */
export const PAGE_SIZES = [20, 50, 100] as const
export type PageSize = (typeof PAGE_SIZES)[number]
const DEFAULT_SIZE: PageSize = 50
/** 찾는 말을 모으는 시간. */
const SETTLE_MS = 250
/** 한 번의 쓰기가 여러 알림을 내므로 모아 한 번 읽는다. */
const REFETCH_MS = 1000

/** 쪽 읽기의 답 — 그 쪽의 줄과, 거르개·찾기에 맞는 전체 수. */
export type Paged = { items: unknown[]; total?: number }

/** 임대를 싣는 읽기가 낡는 알림 — 임대·VM, 그리고 임대가 싣는 소유자의 이름을 바꾸는 `user`/`profile`. */
export const leaseChanged = (c: Change): boolean =>
  c.kind === 'lease' || c.kind === 'vm' || (c.kind === 'user' && c.what === 'profile')

/** 사람을 싣는 읽기가 낡는 알림 — 계정(이름·역할·직위·탈퇴)과, 팀 칸·팀 거르개·닿을 수 있는 사람을 바꾸는 팀. */
export const peopleChanged = (c: Change): boolean => c.kind === 'user' || c.kind === 'team'

/** 친 말을 모은 값 — 그동안 바뀌지 않아야 따라간다. 처음 값은 기다리지 않는다. 상단 바의 찾기도 이것으로 모은다. */
export function useSettled(typed: string): string {
  const [settled, setSettled] = useState(typed)
  useEffect(() => {
    if (typed === settled) return
    const timer = window.setTimeout(() => setSettled(typed), SETTLE_MS)
    return () => window.clearTimeout(timer)
  }, [typed, settled])
  return settled
}

export type Page<R> = {
  data: R | null
  error: Error | null
  loading: boolean
  refresh: () => void
  /** 1부터. */
  page: number
  pages: number
  size: PageSize
  setPage: (page: number) => void
  /** 크기를 바꾸면 1쪽으로 돌아간다. */
  setSize: (size: PageSize) => void
  /** 지금 읽는 목록과 쪽 — 거르개·정렬·찾는 말·쪽·크기 중 하나가 바뀌면 바뀐다. 쪽에 매인 상태(고르기)를 비우는 열쇠다. */
  at: string
}

export function usePage<R extends Paged>({
  read,
  terms,
  filters,
  match,
  pollMs,
  size: first = DEFAULT_SIZE,
}: {
  /** 한 쪽을 읽는다. 찾는 말은 모은 뒤의 것이다. */
  read: (q: { terms: string[]; limit: number; offset: number }) => Promise<R>
  /** 지금 친 찾는 말. */
  terms: string[]
  /** 목록을 정하는 나머지(거르개·정렬). 값으로 견준다. */
  filters: unknown
  match: (c: Change) => boolean
  /** 스트림이 없을 때의 간격. 붙어 있으면 `useFallback` 이 늦춘다. */
  pollMs: number
  /** 처음 쪽의 크기. */
  size?: PageSize
}): Page<R> {
  const settled = useSettled(JSON.stringify(terms))

  const list = JSON.stringify([filters, settled])
  const [size, setSizeState] = useState<PageSize>(first)
  const [page, setPage] = useState(1)
  // 다른 목록이면 1쪽부터다. 렌더 중에 고쳐 두므로 옛 쪽 번호로 새 목록을 묻는 읽기는 나가지 않는다.
  const [seen, setSeen] = useState(list)
  if (seen !== list) {
    setSeen(list)
    setPage(1)
  }

  const poll = usePoll(
    () => read({ terms: JSON.parse(settled) as string[], limit: size, offset: (page - 1) * size }),
    useFallback(pollMs),
    [list, page, size],
  )
  useRefetchOn(match, poll.refresh, REFETCH_MS)
  useRefreshAfterWrites(poll.refresh)

  const pages = Math.max(1, Math.ceil((poll.data?.total ?? 0) / size))
  // 목록이 줄어 지금 쪽이 끝을 넘으면 마지막 쪽으로 당긴다 — 끝 너머의 쪽은 빈 쪽이다.
  if (page > pages) setPage(pages)

  const setSize = (next: PageSize) => {
    setSizeState(next)
    setPage(1)
  }
  return { ...poll, page, pages, size, setPage, setSize, at: JSON.stringify([list, page, size]) }
}
